import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    Renderer2,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { DomHandler } from 'primeng/dom';

import { ButtonTypesEnum } from '../../enums/button-types.enum';

/**
 * What a menu item's command is called with: PrimeNG's own event, plus whatever the button
 * was given as [data] - which is how a row hands its subject to the menu it opens.
 */
export interface SplitButtonCommandEvent extends MenuItemCommandEvent {
    data?: any;
}


@Component({
    selector: 'agpr-split-button',
    templateUrl: './split-button.component.html',
    styleUrls: ['./split-button.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0,
                transform: 'scaleY(0)'
            })),
            state('visible', style({
                opacity: 1,
                transform: 'scaleY(1)'
            })),
            transition('visible => hidden', animate('.1s linear')),
            transition('hidden => visible', animate('.15s cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class SplitButtonComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    @Input() id: string;
    @Input() primary: boolean = false;
    @Input() style: ButtonTypesEnum = ButtonTypesEnum.primaryB;
    @Input() label?: string;
    @Input() tooltip?: string;
    @Input() tooltipPosition?: string = 'right';
    @Input() icon?: string;
    @Input() disabled?: boolean = false;
    @Input() loading: boolean;
    @Input() panelPositionY?: string = 'bottom';
    @Input() panelPositionX?: string = 'left';
    @Input() panelWidth?: number;
    @Input() caretDown: boolean = true;
    @Input() get options(): MenuItem[] {
        return this._options;
    }
    @Input() data?: any;
    set options(options: MenuItem[]) {
        this._options = options;
        this.optionsToDisplay = this._options;
        this.optionsChanged = true;
    }
    @Input() showTriggerIcon?: boolean = true;
    @Input() primaryTriggerIcon?: boolean = false;

    @ViewChild('panel') panelViewChild: ElementRef;
    @ViewChild('itemswrapper') itemsWrapperViewChild: ElementRef;
    @ViewChild('button') buttonViewChild: ElementRef;
    @ViewChild('splitButton') splitButtonViewChild: ElementRef;

    readonly buttonTypes = ButtonTypesEnum;

    public selfClick: boolean;
    public itemClick: boolean;
    public panelVisible: boolean = false;
    public panel: HTMLDivElement;
    public button: HTMLDivElement;
    public splitButton: HTMLDivElement;
    public shown: boolean;
    public itemTemplate: TemplateRef<any>;
    public optionsToDisplay: any[];
    public documentClickListener: any;
    public optionsChanged: boolean;
    public itemsWrapper: HTMLDivElement;
    public initialized: boolean;
    public scrollableParents: any;

    private _options: MenuItem[];

    constructor(
        public el: ElementRef,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public zone: NgZone
    ) { }

    private readonly scrollListener = () => this.hide();

    ngAfterViewInit() {
        this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
        this.button = <HTMLDivElement>this.buttonViewChild.nativeElement;
        this.itemsWrapper = <HTMLDivElement>this.itemsWrapperViewChild.nativeElement;
        this.splitButton = <HTMLDivElement>this.splitButtonViewChild.nativeElement;
        this.initialized = true;

        this.splitButton.appendChild(this.panel);
    }

    onItemClick(event, item: MenuItem) {
        event.stopImmediatePropagation();
        this.itemClick = true;
        this.selectItem(event, item);
    }

    selectItem(event, item: MenuItem) {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        if (!item.url) {
            event.preventDefault();
        }

        if (item.command) {
            const commandParams: SplitButtonCommandEvent = {
                originalEvent: event,
                item: item
            };
            if (this.data) {
                commandParams.data = this.data;
            }
            item.command(commandParams);
        }
        this.itemClick = false;
        this.hide();
    }

    ngAfterViewChecked() {
        if (this.shown) {
            this.onShow();
            this.shown = false;
        }

        if (this.optionsChanged && this.panelVisible) {
            this.optionsChanged = false;

            this.zone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.alignPanel();
                }, 1);
            });
        }

        if (this.itemsWrapper) {
            const selectedItem = DomHandler.findSingle(this.panel, 'li.ui-state-highlight');
            if (selectedItem) {
                DomHandler.scrollInView(this.itemsWrapper, DomHandler.findSingle(this.panel, 'li.ui-state-highlight'));
            }
        }
    }

    onMouseClick() {
        if (this.disabled) {
            return;
        }

        this.selfClick = true;
        if (!this.itemClick) {
            if (this.panelVisible) {
                this.hide();
            } else {
                this.show();
            }
        }
    }

    onShow() {
        this.bindDocumentClickListener();
        this.addEventListeners();

        if (this.options && this.options.length) {
            this.alignPanel();

            const selectedListItem = DomHandler.findSingle(this.itemsWrapper, '.ui-dropdown-item.ui-state-highlight');
            if (selectedListItem) {
                DomHandler.scrollInView(this.itemsWrapper, selectedListItem);
            }
        }
    }

    show() {
        // hide all the other split panel before showing
        const otherPanels = DomHandler.find(document, '.agpr-splitButton-panel');
        otherPanels.forEach(panel => {
            panel.style.opacity = 0;
        });

        this.panel.style.zIndex = String(++DomHandler.zindex);
        this.panelVisible = true;
        this.shown = true;
        this.cd.markForCheck();

        this.alignPanel();
    }

    hide() {
        this.panelVisible = false;
    }

    alignPanel() {
        const buttonRect = this.button.getBoundingClientRect();

        if (this.panelWidth) {
            this.panel.style.width = this.panelWidth + 'px';
        } else {
            this.panel.style.minWidth = buttonRect.width + 'px';
        }

        if (this.panelPositionX === 'right') {
            this.panel.style.right = '0'; // negative difference between panel and button
            this.panel.style.textAlign = 'right';
        } else {
            this.panel.style.left = '0';
            this.panel.style.textAlign = 'left';
        }

        if (this.panelPositionY === 'top') {
            this.panel.style.bottom = buttonRect.height + 'px';
            this.panel.style.marginBottom = '2px';
            this.panel.style.transformOrigin = 'center bottom';
        } else {
            this.panel.style.top = buttonRect.height + 'px';
            this.panel.style.marginTop = '2px';
            this.panel.style.transformOrigin = 'center top';
        }
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', () => {
                if (!this.selfClick && !this.itemClick) {
                    this.panelVisible = false;
                    this.unbindDocumentClickListener();
                }

                this.selfClick = false;
                this.itemClick = false;
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    ngOnDestroy() {
        this.initialized = false;
        this.unbindDocumentClickListener();
        this.removeEventListeners();

        if (this.panel) {
            this.panel.parentNode.removeChild(this.panel);
        }
    }

    addEventListeners() {
        this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);

        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.scrollListener);
            });
        }
        window.addEventListener('resize', this.scrollListener);
    }

    removeEventListeners() {
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.scrollListener);
            });
        }

        window.removeEventListener('resize', this.scrollListener);
    }

    onFocusOut(event: FocusEvent, item: SplitButtonComponent) {
        if (!!event.relatedTarget && !(event.relatedTarget as HTMLElement).classList?.contains('agpr-splitButton-panel-item')) {
            this.hide();
        }
    }
}
